// LooksPost.js

import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BsMegaphone } from 'react-icons/bs';
import Favorite from '@mui/icons-material/Favorite';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  FaRegPaperPlane,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaFacebook,
  FaSoundcloud
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';           // ← NEW
import { GoAlert } from 'react-icons/go';
import { CiViewList } from 'react-icons/ci';
import { IoMailOutline } from 'react-icons/io5';
import { AiOutlineZoomIn } from 'react-icons/ai';
import LooksReportForm      from './LooksReportForm';
import { LooksReportsContext } from '../contexts/LooksReportsContext';

import { useAuth } from '../contexts/AuthContext';
import { LooksPostsContext } from '../contexts/LooksPostsContext';
import { timeAgo } from '../utils/timeFormatter';
import SharePost from './SharePost';
import { db } from '../utils/firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
import VisibilityTracker from './VisibilityTracker';
import {
  incrementLinkClickCount,
  incrementInstagramClickCount,
  incrementTwitterClickCount,
  incrementTiktokClickCount,
  incrementYoutubeClickCount,
  incrementFacebookClickCount, 
  incrementSoundcloudClickCount  
} from '../utils/dbUtilities';

const TWO_MINUTES = 2 * 60 * 1000;
const SAFE_MARGIN = 96;

/* ---------------- tiny modal ---------------- */
const ZoomModal = ({ src, onClose }) => (
  <div
    role="dialog" aria-modal="true" onClick={onClose}
    style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,.85)',
      display:'flex', justifyContent:'center', alignItems:'center',
      zIndex:9999
    }}
  >
    <div
      onClick={e=>e.stopPropagation()}
      style={{
        position:'relative',
        maxWidth:`calc(100vmin - ${SAFE_MARGIN}px)`,
        maxHeight:`calc(100vmin - ${SAFE_MARGIN}px)`
      }}
    >
      <button
        aria-label="Close" onClick={onClose}
        style={{
          position:'absolute', top:8, right:8,
          background:'rgba(0,0,0,.6)', color:'#fff',
          border:'none', borderRadius:'50%', width:32, height:32,
          fontSize:22, cursor:'pointer', lineHeight:0
        }}
      >×</button>
      <img
        src={src} alt="Profile full"
        style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain', display:'block' }}
      />
    </div>
  </div>
);

/**
 * StyledCard
 * - Removes default border
 * - Keeps a subtle boxShadow for style
 */
const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  border: 'none',
  overflow: 'hidden',
  margin: '24px auto',
  width: '100%',
  maxWidth: '550px',
  minWidth: window.innerWidth <= 1180 ? '300px' : '550px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  height: 'auto',
  minHeight: '300px',
  transition: 'max-width 0.3s ease-in-out, padding 0.3s ease-in-out',
  '& > *': {
    width: '100%',
    boxSizing: 'border-box'
  },
  '& .MuiCardMedia-root': {
    border: 'none',
    outline: 'none',
    boxShadow: 'none'
  },
  [theme.breakpoints.down('md')]: {
    minWidth: '300px',
    maxWidth: '100%'
  },
  '&.small-post': {
    maxWidth: '100%',
    minWidth: '100%',
    margin: '0 0 16px 0',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
  }
}));

const StyledCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '16px',
  width: '100%'
});

// Post Title styling
const StyledTitle = styled(Typography)({
  fontSize: '1.5rem', // ~24px
  fontWeight: 'bold',
  marginBottom: '8px',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer'
});

// Helper to shorten URLs
const shortenUrl = (url) => {
  const urlWithoutPrefix = url.replace(/^https?:\/\/(www\.)?/i, '');
  const maxLength = 25;
  return urlWithoutPrefix.length > maxLength
    ? `${urlWithoutPrefix.slice(0, maxLength)}...`
    : urlWithoutPrefix;
};

/**
 * Splits text into sentences, using `Intl.Segmenter` when available.
 * Falls back to a minimal regex that ignores common abbreviations.
 * @param {string} text
 * @returns {string[]}
 */
const splitText = (text = '') => {
  if (!text.trim()) return [];

  /* ---------- 1 ) modern browsers: native sentence segmentation ---------- */
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    return [
      ...new Intl.Segmenter('en', { granularity: 'sentence' }).segment(text)
    ]
      .map(seg => seg.segment.trim())
      .filter(Boolean);
  }

  /* ---------- 2 ) fallback for very old browsers ---------- */
  const ABBR = [
    'mr.', 'mrs.', 'ms.', 'dr.', 'prof.', 'sr.', 'jr.',
    'inc.', 'ltd.', 'co.', 'vs.', 'etc.', 'e.g.', 'i.e.'
  ];

  return text
    .split(/([.?!])\s+(?=[A-Z])/)
    .reduce((sentences, chunk, idx, arr) => {
      if (idx % 2 === 0) {                            // the text chunk
        const sentence = chunk + (arr[idx + 1] || ''); // add its punctuation
        const lower    = sentence.trim().toLowerCase();

        // If it ends with an abbreviation, glue onto previous sentence
        if (ABBR.some(a => lower.endsWith(` ${a}`))) {
          if (sentences.length) {
            sentences[sentences.length - 1] += ' ' + sentence.trim();
          } else {
            sentences.push(sentence.trim());
          }
        } else {
          sentences.push(sentence.trim());
        }
      }
      return sentences;
    }, [])
    .filter(Boolean);
};

/* Ice-breaker label map (matches LooksPostForm.js) */
const ICE_BREAKER_LABELS = {
  origin:            'Where are you from?',
  favoriteFood:      "What's your favorite food?",
  favCountryVisit:   'Favorite country you’ve visited?',
  musicGenre:        'Which song or artist have you been playing on repeat lately?',
  dreamMeet:         'If you could meet anyone (dead or alive), who would it be?',
  favDessert:        "What's your favorite dessert?",
  millionOrBillion:  'Would you take $1 million guaranteed or flip a coin for $1 billion?'
};

/* Keep the display order identical to LooksPostForm.js */
const ICE_BREAKER_ORDER = [
  'origin',
  'favoriteFood',
  'favCountryVisit',
  'musicGenre',
  'dreamMeet',
  'favDessert',
  'millionOrBillion'
];

/**
 * LooksPost Component
 */
const LooksPost = ({
  id,
  author,
  description,
  city,
  state,
  country,
  location,
  goals,
  timestamp,
  lastEdited,
  link,
  email,
  instagram,
  twitter,
  tiktok,
  youtube,
  facebook,
  soundcloud, 
  isSmall,
  profileImageUrl,
  profileImageFullUrl,
  userName,
  iceBreakers = {},
  onDelete
}) => {
  const navigate = useNavigate();
  const { currentUser, isLoggedIn, isAdmin } = useAuth();
  const { deletePostById } = useContext(LooksPostsContext);
  const { reportPost }     = useContext(LooksReportsContext);

  /* zoom state */
  const [zoomUrl, setZoomUrl] = useState(null);

  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showContactList, setShowContactList] = useState(false);
  const [showSharePost, setShowSharePost] = useState(false);
  const [isAuthor, setIsAuthor] = useState(
    currentUser && currentUser.displayName === author
  );

  // We'll store some analytics in local state
  const [post, setPost] = useState({
    isDeleted: false,
    viewCount: 0,
    linkClickCount: 0,
    impressionCount: 0,
    instagramClickCount: 0,
    twitterClickCount: 0,
    tiktokClickCount: 0,
    youtubeClickCount: 0,
    facebookClickCount: 0,
    soundcloudClickCount: 0,  
    isReported: false,
    feedImpressionCount: 0,
    discoveryBoxImpressionCount: 0,
    pageViewCount: 0
  });

  /* ------------------------------------------------------------------
     1️⃣  figure out which URL we should load in the modal
  ------------------------------------------------------------------ */
  const getFullUrl = useCallback(() => {
    if (profileImageFullUrl) return profileImageFullUrl;       // happy path

    // Legacy fallback: Storage path replacement
    if (profileImageUrl?.includes('looksProfileImagesAvatar/')) {
      return profileImageUrl.replace(
        'looksProfileImagesAvatar/',
        'looksProfileImagesFull/'
      );
    }
    return profileImageUrl;   // last resort (will still be the circle)
  }, [profileImageFullUrl, profileImageUrl]);

  // Decide who can see the like count
  const canSeeLikesCount = () => isAuthor || isAdmin;

  // For localStorage visited styling (clickedPosts)
  const [clickedPosts, setClickedPosts] = useState(() => {
    const saved = localStorage.getItem('clickedPosts');
    return saved ? JSON.parse(saved) : {};
  });

  // === Real-time Firestore Likes ===
  useEffect(() => {
    const likesQueryRef = query(
      collection(db, 'looksLikes'),
      where('postId', '==', id)
    );
    const unsubscribe = onSnapshot(likesQueryRef, (snapshot) => {
      const likesArr = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setLikeCount(likesArr.length);

      if (currentUser) {
        setIsLiked(likesArr.some((like) => like.userId === currentUser.uid));
        setIsAuthor(currentUser.displayName === author);
      }
    });
    return () => unsubscribe();
  }, [id, currentUser, author]);

  // === 2-Minute Polling for Post Analytics ===
  const fetchPostData = useCallback(async () => {
    if (!id) return;
    try {
      const docRef = doc(db, 'looksPosts', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setPost((prev) => ({
          ...prev,
          viewCount: data.viewCount || 0,
          linkClickCount: data.linkClickCount || 0,
          impressionCount: data.impressionCount || 0,
          instagramClickCount: data.instagramClickCount || 0,
          twitterClickCount: data.twitterClickCount || 0,
          tiktokClickCount: data.tiktokClickCount || 0,
          youtubeClickCount: data.youtubeClickCount || 0,
          facebookClickCount: data.facebookClickCount || 0,
          soundcloudClickCount: data.soundcloudClickCount || 0,
          isReported: data.isReported || false,
          feedImpressionCount: data.feedImpressionCount || 0,
          discoveryBoxImpressionCount: data.discoveryBoxImpressionCount || 0,
          pageViewCount: data.pageViewCount || 0
        }));
      }
    } catch (err) {
      console.error('Error fetching post data:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchPostData();
    const intervalId = setInterval(() => {
      fetchPostData();
    }, TWO_MINUTES);
    return () => clearInterval(intervalId);
  }, [fetchPostData]);

  // Keep localStorage "clickedPosts" in sync
  useEffect(() => {
    const handleStorage = () => {
      const saved = localStorage.getItem('clickedPosts');
      if (saved) {
        setClickedPosts(JSON.parse(saved));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // === Like / Unlike Logic ===
  const handleLike = async (event) => {
    event.stopPropagation();

    if (!isLoggedIn || !currentUser || !id) {
      navigate('/login');
      return;
    }

    /* ---------- optimistic UI ---------- */
    setIsLiked((prev) => !prev);                          // toggle heart colour immediately
    setLikeCount((prev) => (isLiked ? prev - 1 : prev + 1)); // jump the counter instantly

    if (isLiked) {
      // ----- UNLIKE -----
      const likeQueryRef = query(
        collection(db, 'looksLikes'),
        where('postId', '==', id),
        where('userId', '==', currentUser.uid)
      );
    
      const snapshot = await getDocs(likeQueryRef);
      const likeDoc  = snapshot.docs[0];
    
      if (likeDoc) {
        await deleteDoc(likeDoc.ref);
      }
    } else {
      // ----- LIKE -----
      await addDoc(collection(db, 'looksLikes'), {
        postId:   id,
        userId:   currentUser.uid,
        timestamp: new Date()
      });
    }  
  };

  // === Navigation & Click Handlers ===
  const handleUsernameClick = (e) => {
    e.stopPropagation();
    navigate(`/looksusersposts/${author}`);
  };

  const navigateToPostPage = () => {
    navigate(`/looksposts/${id}`);
  };

  const handleTitleClick = (e) => {
    e.stopPropagation();
    const updatedClickedPosts = { ...clickedPosts, [id]: true };
    setClickedPosts(updatedClickedPosts);
    localStorage.setItem('clickedPosts', JSON.stringify(updatedClickedPosts));
    navigateToPostPage();
  };

  // Social link click handlers
  const handleLinkClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(
      link && link.includes('http') ? link : `https://${link}`,
      '_blank',
      'noopener,noreferrer'
    );
    incrementLinkClickCount(id, 'looksPosts');
  };

  const handleInstagramClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (instagram) {
      incrementInstagramClickCount(id, 'looksPosts');
      window.open(
        `https://instagram.com/${instagram.replace('@', '')}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const handleTwitterClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (twitter) {
      incrementTwitterClickCount(id, 'looksPosts');
      window.open(
        `https://twitter.com/${twitter.replace('@', '')}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const handleTiktokClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (tiktok) {
      incrementTiktokClickCount(id, 'looksPosts');
      window.open(
        `https://tiktok.com/@${tiktok.replace('@', '')}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const handleYoutubeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (youtube) {
      incrementYoutubeClickCount(id, 'looksPosts');
      window.open(
        `https://youtube.com/@${youtube.replace('@', '')}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const handleFacebookClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (facebook) {
      incrementFacebookClickCount(id, 'looksPosts');
      window.open(
        `https://www.facebook.com/${facebook}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const handleSoundcloudClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (soundcloud) {
      incrementSoundcloudClickCount(id, 'looksPosts');      // ★ add
      window.open(
        `https://soundcloud.com/${soundcloud.replace('@', '')}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };
  
  const navigateToCityPosts = (c) => {
    navigate(`/cityposts/${c}`);
  };
  const navigateToStatePosts = (s) => {
    navigate(`/stateposts/${s}`);
  };
  const navigateToCountryPosts = (ctr) => {
    navigate(`/countryposts/${ctr}`);
  };
  const navigateToLocationPosts = (loc) => {
    navigate(`/locationposts/${loc}`);
  };

  // Reporting
  const toggleReportForm = (e) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    setShowReportForm(!showReportForm);
  };

  // Contact List
  const toggleContactList = (e) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    setShowContactList(!showContactList);
  };
  const closeContactList = () => {
    setShowContactList(false);
  };

  // Sharing
  const toggleSharePost = (e) => {
    e.stopPropagation();
    setShowSharePost((prev) => !prev);
  };
  const closeSharePost = () => {
    setShowSharePost(false);
  };

  // Delete post
  const handleRemove = async (e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deletePostById(id);
        if (onDelete) {
          onDelete(id);
        }
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  // Edit post
  const handleEdit = (e) => {
    e.stopPropagation();
    navigate(`/editlookspost/${id}`);
  };

  // If post is flagged as deleted in Firestore, hide it
  if (post.isDeleted) return null;

  // If post is reported AND user is not admin => center disclaimer
  if (post.isReported && !isAdmin) {
    return (
      <VisibilityTracker postId={id} isDiscoveryBox={false} isPostAuthor={false}>
        <StyledCard
          sx={{
            margin: '24px auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px'
          }}
        >
          <CardContent
            sx={{
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Reported Post Under Review
            </Typography>
            <Typography variant="body1">
              This post has been flagged and is under Admin review.
            </Typography>
          </CardContent>
        </StyledCard>
      </VisibilityTracker>
    );
  }

  return (
    <VisibilityTracker
      postId={id}
      isDiscoveryBox={false}
      isPostAuthor={currentUser?.uid === post.authorId}
    >
      {/* ───────────────────────────
          HyroClipse‑style card wrapper
          (no onClick ­– only explicit
          elements like the title navigate)
      ─────────────────────────── */}
      <StyledCard className={isSmall ? 'small-post' : ''}>
        <StyledCardContent>
          {/* ---------- Headline (userName) & like ---------- */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <StyledTitle
              onClick={handleTitleClick}
              sx={{
                color: clickedPosts[id] ? '#551A8B' : '#1a0dab',
                marginBottom: 0
              }}
            >
              {userName || 'Anonymous'}
            </StyledTitle>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handleLike}>
                {isLiked ? (
                  <Favorite sx={{ color: '#ed4956' }} />
                ) : (
                  <FavoriteBorder sx={{ color: '#262626' }} />
                )}
              </IconButton>
              {canSeeLikesCount() && (
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, textAlign: 'center', minWidth: '15px' }}
                >
                  {likeCount}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ mb: 1 }} />

          {/* ───── Avatar + click-to-zoom ───── */}
          {profileImageUrl && (
            <Box sx={{ display:'flex', justifyContent:'center', mt:1, mb:2, position:'relative' }}>
              <Box
                sx={{ width:300, height:300, borderRadius:'50%', overflow:'hidden', cursor:'pointer' }}
                onClick={(e)=>{ e.stopPropagation(); setZoomUrl(getFullUrl()); }}
              >
                <img
                  src={profileImageUrl}
                  alt="Profile avatar"
                  style={{ width:'100%', height:'100%', objectFit:'cover' }}
                />
              </Box>
              {/* 🔄 📍 overlay icon is now clickable & stops bubbling */}
              <Box
                onClick={(e)=>{                                    // 📍 NEW
                  e.stopPropagation();                             // stop card navigation
                  setZoomUrl(getFullUrl());                       // open modal
                }}                                                 // 📍 NEW
                sx={{
                  position:'absolute', bottom:10, right:10,
                  display:{ xs:'none', sm:'flex' },
                  width:36, height:36, borderRadius:'50%',
                  background:'rgba(0,0,0,.6)',
                  justifyContent:'center', alignItems:'center',
                  cursor:'pointer'
                }}
              >
                <AiOutlineZoomIn style={{ color:'#fff', fontSize:20 }}/>
              </Box>
            </Box>
          )}

          {/* ---------- "Who Are You?" heading ---------- */}
          <Typography
            variant="h6"
            sx={{
              textAlign: 'left',
              fontWeight: 'bold',
              mt: 2,
              mb: 1,
              color: '#000'
            }}
          >
            Who Are You?
          </Typography>

          {/* ---------- Description ---------- */}
          <Typography
            variant="body1"
            sx={{ whiteSpace: 'pre-line', mt: 1, textAlign: 'left' }}
          >
            {splitText(description).length > 1 ? (
              <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
                {splitText(description).map((sentence, idx) => (
                  <li key={idx}>{sentence.trim()}</li>
                ))}
              </ul>
            ) : (
              description
            )}
          </Typography>

          {/* ---------- Ice-breakers ---------- */}
          {Object.keys(iceBreakers).length > 0 && (
            <>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Fun Facts
              </Typography>

              {/* one line per answer, no bullets */}
              <Box sx={{ mb: 2 }}>
                {ICE_BREAKER_ORDER.map((id) => {
                  const answer = iceBreakers[id];
                  if (!answer) return null;
                  return (
                    <Typography key={id} variant="body2" sx={{ mb: 0.5, textAlign: 'left' }}>
                      <strong>{ICE_BREAKER_LABELS[id]}</strong> {answer}
                    </Typography>
                  );
                })}
              </Box>
            </>
          )}

          {/* Where Are You? */}
          {(city || state || country || location) && (
            <Box sx={{ mt: 3, mb: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  textAlign: 'left',
                  color: '#000',
                  mb: 1
                }}
              >
                Where Are You?
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '1rem',
                  textAlign: 'left',
                  display: 'inline-block',
                  lineHeight: '1.4'
                }}
              >
                {city && (
                  <>
                    <span
                      className="location-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToCityPosts(city);
                      }}
                      style={{ cursor: 'pointer', textDecoration: 'none' }}
                    >
                      {city}
                    </span>
                  </>
                )}
                {city && state ? ', ' : ''}
                {state && (
                  <>
                    <span
                      className="location-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToStatePosts(state);
                      }}
                      style={{ cursor: 'pointer', textDecoration: 'none' }}
                    >
                      {state}
                    </span>
                  </>
                )}
                {(city || state) && country ? ', ' : ''}
                {country && (
                  <>
                    <span
                      className="location-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToCountryPosts(country);
                      }}
                      style={{ cursor: 'pointer', textDecoration: 'none' }}
                    >
                      {country}
                    </span>
                  </>
                )}
                {location && (
                  <>
                    {' '}
                    (
                    <span
                      className="location-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToLocationPosts(location);
                      }}
                      style={{ cursor: 'pointer', textDecoration: 'none' }}
                    >
                      {location}
                    </span>
                    )
                  </>
                )}
              </Typography>
            </Box>
          )}

          {/* Link */}
          {link && (
            <Box
              sx={{
                mt: 0,
                mb: 1,
                width: '100%',
                textAlign: 'left'
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: '#1a0dab',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  fontSize: '1rem'
                }}
                onClick={handleLinkClick}
              >
                {shortenUrl(link)}
              </Typography>
            </Box>
          )}

                    {/* Posted by + Last Edited */}
                    <Typography
            variant="body2"
            color="text.secondary"
            onClick={handleUsernameClick}
            sx={{
              cursor: 'pointer',
              mb: lastEdited ? 0.5 : 2,
              textAlign: 'left',
              mt: link ? 0 : 1
            }}
          >
            Posted {timeAgo(timestamp?.toDate ? timestamp.toDate() : timestamp)} by{' '}
            <span
              style={{
                color: '#1a0dab',
                fontWeight: 'bold',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
            >
              {author}
            </span>
          </Typography>
          {lastEdited && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, textAlign: 'left' }}
            >
              Last Edited {timeAgo(lastEdited?.toDate ? lastEdited.toDate() : lastEdited)}
            </Typography>
          )}

          {/* Total Views: sum of old + new fields */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              mt: 2,
              mb: 2
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                fontSize: '15px',
                lineHeight: 1.4,
                fontWeight: 700,
                textAlign: 'center'
              }}
            >
              <span
                style={{
                  backgroundColor: '#fff',
                  color: '#000',
                  fontSize: '15px',
                  padding: '3px 8px',
                  border: '2px solid #000',
                  borderRadius: '6px',
                  marginRight: '8px'
                }}
              >
                {(
                  (post.impressionCount || 0) +
                  (post.feedImpressionCount || 0) +
                  (post.discoveryBoxImpressionCount || 0) +
                  (post.pageViewCount || 0)
                ).toLocaleString()}
              </span>
               Views
            </Typography>
          </Box>

          {/* Goals */}
          {goals && Array.isArray(goals) && goals.length > 0 && (
            <>
              <Typography
                variant="h6"
                sx={{
                  textAlign: 'left',
                  fontWeight: 'bold',
                  color: '#000',
                  mt: 2,
                  mb: 1
                }}
              >
                What's The Goal?
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mt: 1 }}>
                {goals.slice(0, 2).map((goal) => (
                  <Box
                    key={goal}
                    sx={{
                      cursor: 'default',
                      backgroundColor: '#e0f4ff',
                      p: '5px 12px',
                      borderRadius: '16px',
                      fontSize: '14px',
                      color: '#333',
                      border: '1px solid #99e0ff',
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {goal}
                  </Box>
                ))}
              </Box>

              {goals.length > 2 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    mt: 1,
                    mb: 2
                  }}
                >
                  {goals.slice(2).map((goal) => (
                    <Box
                      key={goal}
                      sx={{
                        cursor: 'default',
                        backgroundColor: '#e0f4ff',
                        p: '5px 12px',
                        borderRadius: '16px',
                        fontSize: '14px',
                        color: '#333',
                        border: '1px solid #99e0ff',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {goal}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}

          {/* Follow the Growth */}
          {(instagram || twitter || tiktok || youtube || facebook || soundcloud) && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                mt: 3,
                mb: 2,
                width: '100%',
                gap: 2
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#000',
                  whiteSpace: 'nowrap'
                }}
              >
                Follow the Growth!
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {instagram && (
                  <FaInstagram
                    className="social-icon instagram"
                    onClick={handleInstagramClick}
                    style={{ fontSize: '28px', cursor: 'pointer' }}
                  />
                )}
                {twitter && (
                  <FaXTwitter
                    className="social-icon twitter"
                    onClick={handleTwitterClick}
                    style={{ fontSize: '28px', cursor: 'pointer' }}
                  />
                )}
                {tiktok && (
                  <FaTiktok
                    className="social-icon tiktok"
                    onClick={handleTiktokClick}
                    style={{ fontSize: '28px', cursor: 'pointer' }}
                  />
                )}
                {youtube && (
                  <FaYoutube
                    className="social-icon youtube"
                    onClick={handleYoutubeClick}
                    style={{ fontSize: '28px', cursor: 'pointer' }}
                  />
                )}
                {facebook && (
                  <FaFacebook
                    className="social-icon facebook"
                    onClick={handleFacebookClick}
                    style={{ fontSize: '28px', cursor: 'pointer' }}
                  />
                )}
                {soundcloud && (                                   
                  <FaSoundcloud
                    className="social-icon soundcloud"
                    onClick={handleSoundcloudClick}
                    style={{ fontSize: '28px', cursor: 'pointer' }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Contact List, Share, Email, etc. */}
          <Box sx={{ mt: 4, mb: '10px', width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                mb: 2
              }}
            >
              <IconButton
                onClick={toggleContactList}
                sx={{ p: '4px', color: '#1a0dab', fontSize: '24px' }}
              >
                <GoAlert style={{ fontSize: '24px' }} />
              </IconButton>
              <Typography
                variant="body2"
                onClick={toggleContactList}
                sx={{
                  color: '#1a0dab',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                Email Updates Sign Up
              </Typography>
            </Box>

            {isAuthor && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  mb: 2
                }}
              >
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/view-contact-list');
                  }}
                  sx={{ p: '4px', color: '#1a0dab' }}
                >
                  <CiViewList style={{ fontSize: '24px' }} />
                </IconButton>
                <Typography
                  variant="body2"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/view-contact-list');
                  }}
                  sx={{
                    color: '#1a0dab',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                >
                  View Email Sign Ups
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                mb: 2
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowSharePost(true);
              }}
            >
              <IconButton
                sx={{ p: '4px', color: '#1a0dab', fontSize: '24px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSharePost(true);
                }}
              >
                <BsMegaphone style={{ fontSize: '24px' }} />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: '#1a0dab',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Share this Post
              </Typography>
            </Box>
          </Box>

          {showSharePost && (
            <Box
              sx={{
                position: 'relative',
                p: '15px 20px',
                pt: '50px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: '#f9f9f9',
                mb: '20px'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSharePost(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  color: '#666',
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                  fontWeight: 'bold'
                }}
                aria-label="Close Share Options"
              >
                ×
              </button>
              <SharePost url={`${window.location.origin}/looksposts/${id}`} />
            </Box>
          )}

          {email && (
            <Box
              sx={{
                mb: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`mailto:${email}`, '_blank', 'noopener,noreferrer');
                }}
                sx={{ p: '4px', color: '#1a0dab', fontSize: '24px' }}
              >
                <IoMailOutline style={{ fontSize: '24px' }} />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: '#1a0dab',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`mailto:${email}`, '_blank', 'noopener,noreferrer');
                }}
              >
                Email
              </Typography>
            </Box>
          )}

          {/* Author's Post Analytics */}
          {isAuthor && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                p: '20px 16px',
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                mt: '20px',
                gap: '8px',
                width: '100%',
                backgroundColor: '#f8f9fa'
              }}
            >
              <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
                Your Post Analytics
              </Typography>
              <Typography variant="body2" sx={{ color: '#000' }}>
                Post Page Views: {post.viewCount || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#000' }}>
                URL Link Clicks: {post.linkClickCount || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#000' }}>
                Post Impressions (Views in Feeds & Discovery Boxes): {post.impressionCount || 0}
              </Typography>
              {instagram && (
                <Typography variant="body2" sx={{ color: '#000' }}>
                  Instagram Clicks: {post.instagramClickCount || 0}
                </Typography>
              )}
              {twitter && (
                <Typography variant="body2" sx={{ color: '#000' }}>
                  Twitter Clicks: {post.twitterClickCount || 0}
                </Typography>
              )}
              {tiktok && (
                <Typography variant="body2" sx={{ color: '#000' }}>
                  TikTok Clicks: {post.tiktokClickCount || 0}
                </Typography>
              )}
              {youtube && (
                <Typography variant="body2" sx={{ color: '#000' }}>
                  YouTube Clicks: {post.youtubeClickCount || 0}
                </Typography>
              )}
              {facebook && (
                <Typography variant="body2" sx={{ color: '#000' }}>
                  Facebook Clicks: {post.facebookClickCount || 0}
                </Typography>
              )}
              {soundcloud && (
                <Typography variant="body2" sx={{ color: '#000' }}>
                  SoundCloud Clicks: {post.soundcloudClickCount || 0}
                </Typography>
              )}
            </Box>
          )}
        </StyledCardContent>

        {/* Bottom Bar: Edit / Delete (author or admin)  OR  Report */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          {isAuthor || isAdmin ? (
            <>
              {/* authors keep the Edit button */}
              {isAuthor && (
                <IconButton onClick={handleEdit}>
                  <EditIcon />
                </IconButton>
              )}

              {/* authors or admins can delete */}
              <IconButton onClick={handleRemove}>
                <DeleteIcon />
              </IconButton>
            </>
          ) : (
            <Tooltip title="Report">
              <IconButton onClick={toggleReportForm}>
                <OutlinedFlagIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Report Form Modal — NEW */}
        {showReportForm && (
          <Box onClick={(e) => e.stopPropagation()}>
            <LooksReportForm
              postId={id}
              submitReport={reportPost}
              closeModal={() => setShowReportForm(false)}
            />
          </Box>
        )}
      </StyledCard>

      {/* zoom overlay */}
      {zoomUrl && <ZoomModal src={zoomUrl} onClose={()=>setZoomUrl(null)} />}
    </VisibilityTracker>
  );
};

export default LooksPost;
      